import {
  BREAKPOINT_SMALL,
  BREAKPOINT_EXTRA_EXTRA_SMALL,
  ROLE_TYPES,
} from "../../../config";
import { PersonV2, RoleV2 } from "../../../gql";
import {
  Box,
  Button,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  ButtonBase,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import React, { useState } from "react";
import { useHistory, useParams } from "react-router";

import { ActionButton } from "../../../components/ActionButton";
import { AddUserV2 } from "./AddUserV2";
import { BorderContainer } from "../../../components/BorderContainer";
import CheckIcon from "@mui/icons-material/Check";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { RevokeInvitationV2 } from "./RevokeInvitationV2";
import { Tab } from "../../../components/Tab";
import { Tabs } from "../../../components/Tabs";
import { UserRoleV2 } from "./UserRoleV2";
import { useFeatures } from "../../../hooks/useFeatures";
import { TransferOwnershipModalV2 } from "./TransferOwnershipModalV2";
import { useGetMyRoleV2, useGetRolesV2 } from "../../../hooks/useGetRolesV2";

interface IRouteParams {
  boardId: string;
}

const useStyles = makeStyles({
  ownerBorderContainer: {
    marginBottom: -2,
  },
  transferOwnershipContainer: {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    borderColor: "#585f64",
    borderWidth: 2,
  },
  titleAndRoleContainer: {
    display: "flex",
    alignItems: "center",
    [`@media(max-width: ${BREAKPOINT_EXTRA_EXTRA_SMALL})`]: {
      justifyContent: "space-between",
    },
  },
  container: {
    maxWidth: 736,
    [`@media(max-width: ${BREAKPOINT_SMALL})`]: {
      maxWidth: "100%",
    },
  },
  titleBox: {
    minWidth: 300,
    marginRight: 8,
    [`@media(max-width: ${BREAKPOINT_SMALL})`]: {
      minWidth: 200,
    },
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    fontFamily: "HelveticaNeue",
  },
  plus: {
    width: 18,
    height: 15,
  },
  roleDescription: {
    padding: "2em 4em",
  },
  listHeader: {
    fontWeight: "bold",
  },
  addUserItemContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const roleFeatures = {
  User: [
    "Create messages",
    "Schedule messages",
    "Edit messages",
    "Favorite messages",
  ],
  Admin: [
    "Set quiet hours",
    "Set timezone",
    "Add or remove users",
    "Manage network connection",
  ],
  Owner: ["Delete board", "Manage billing", "Transfer ownership"],
};

export const UsersV2 = () => {
  const [activeRoleTab, setActiveRoleTab] = useState<number>(0);
  const [removeRoleId, setRemoveRoleId] = useState<null | string>(null);
  const [revokeUserId, setRevokeUserId] = useState<null | string>(null);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);

  const { features } = useFeatures();
  const history = useHistory();
  const params = useParams<IRouteParams>();

  const { data, loading } = useGetRolesV2(params.boardId);
  const { data: myData, loading: myDataLoading } = useGetMyRoleV2(
    params.boardId
  );

  const classes = useStyles();
  const [isAddingUser, setIsAddingUser] = React.useState(false);

  if (loading || !data || myDataLoading || !myData) {
    return <CircularProgress />;
  }

  const roles =
    data?.BoardV2?.__typename === "BoardV2"
      ? data?.BoardV2?.listRoles?.roles || []
      : [];
  const sortedBoardMembers = [
    ...roles.filter((role) => role.permission === "Owner"),
    ...roles.filter((role) => role.permission !== "Owner"),
  ];

  // const myId = data.viewer && data.viewer.account && data.viewer.account.id;
  const myPerson =
    myData?.viewerV2?.__typename === "PersonV2" ? myData.viewerV2 : undefined;
  const myRole = myPerson ? (myPerson?.myRoleForBoard as RoleV2) : undefined;
  const iAmOwner = myRole?.permission === "Owner";
  const iAmAdmin = myRole?.permission === "Admin" || iAmOwner;
  return (
    <>
      <Dialog
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        maxWidth="lg"
      >
        <DialogContent>
          <Tabs onChange={setActiveRoleTab} value={activeRoleTab || 0}>
            {ROLE_TYPES.map((role) => (
              <Tab label={role} key={role} />
            ))}
          </Tabs>
          <Box className={classes.roleDescription}>
            {activeRoleTab !== 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      gutterBottom
                      className={classes.listHeader}
                    >
                      User Features:
                    </Typography>
                  }
                />
              </ListItem>
            )}
            {roleFeatures.User.map((feature) => (
              <ListItem key={feature}>
                <ListItemIcon>
                  <CheckIcon color="secondary" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
            {activeRoleTab !== 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      gutterBottom
                      className={classes.listHeader}
                    >
                      Plus:
                    </Typography>
                  }
                />
              </ListItem>
            )}
            {activeRoleTab !== 0 &&
              [
                ...roleFeatures.Admin,
                ...(activeRoleTab === 2 ? roleFeatures.Owner : []),
              ].map((feature) => (
                <ListItem key={feature}>
                  <ListItemIcon>
                    <CheckIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
          </Box>
        </DialogContent>
      </Dialog>
      <RevokeInvitationV2
        title="Revoke Invitation"
        message="Are you sure you want to revoke this invitation?"
        removeRoleId={revokeUserId}
        onCancel={() => {
          setRevokeUserId(null);
        }}
        boardId={params.boardId}
      />
      <RevokeInvitationV2
        title="Remove User"
        message="Are you sure you want to remove this user?"
        removeRoleId={removeRoleId}
        onCancel={() => {
          setRemoveRoleId(null);
        }}
        boardId={params.boardId}
      />
      <Box className={classes.container}>
        {sortedBoardMembers
          // .filter((member) => member.invitationStatus !== "Revoked")
          .map((member, index) => {
            const person = member.person as PersonV2;
            const isMe = myPerson?.id === person.id;
            const isOwner = member.permission === "Owner";

            return (
              <Box key={member.id}>
                <Box style={{ zIndex: index }}>
                  <BorderContainer
                    responsive
                    className={
                      iAmOwner && isMe ? classes.ownerBorderContainer : ""
                    }
                  >
                    <Box className={classes.titleAndRoleContainer}>
                      <Box className={classes.titleBox}>
                        <Typography className={classes.title}>
                          {member.person.firstName
                            ? `${member.person.firstName || ""} ${
                                member.person.lastName || ""
                              }`
                            : member.person.emailAddress}
                        </Typography>
                      </Box>
                      {person.id && (
                        <UserRoleV2
                          key={`${myDataLoading}-${loading}-role-${index}`}
                          role={{
                            permission: member.permission,
                            id: member.id,
                          }}
                          personId={person.id}
                          disabled={isMe || !iAmAdmin}
                          boardId={params.boardId}
                        />
                      )}
                    </Box>
                    {isMe && features.userProfile ? (
                      <ActionButton
                        onClick={() => {
                          history.push(`/v2/board/${params.boardId}/profile`);
                        }}
                      >
                        Edit Profile
                      </ActionButton>
                    ) : !member.person.firstName ? (
                      features.settings.modifyUsers ? (
                        <ActionButton
                          onClick={() => {
                            setRevokeUserId(member.id);
                          }}
                          buttonColor="danger"
                          disabled={!iAmAdmin}
                        >
                          Revoke Invite
                        </ActionButton>
                      ) : (
                        <Box />
                      )
                    ) : features.settings.modifyUsers && !isOwner ? (
                      <ActionButton
                        onClick={() => {
                          setRemoveRoleId(member.id);
                        }}
                        disabled={!iAmAdmin}
                      >
                        Remove
                      </ActionButton>
                    ) : (
                      <Box />
                    )}
                  </BorderContainer>
                </Box>
                {iAmOwner && isMe ? (
                  <Box style={{ zIndex: index - 1 }}>
                    <BorderContainer
                      responsive
                      className={classes.transferOwnershipContainer}
                    >
                      <ButtonBase
                        onClick={() => {
                          setShowTransferModal(true);
                        }}
                        style={{
                          width: "100%",
                        }}
                      >
                        Transfer Ownership
                      </ButtonBase>
                    </BorderContainer>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            );
          })}
        {iAmAdmin &&
          features.settings.modifyUsers &&
          (isAddingUser ? (
            <AddUserV2
              handleClose={() => setIsAddingUser(false)}
              boardId={params.boardId}
              refetchUsers={() => {}}
              showInfoModal={showInfoModal}
              openInfoModal={() => setShowInfoModal(true)}
              iAmAdmin={iAmAdmin || false}
            />
          ) : (
            <BorderContainer>
              <Box className={classes.addUserItemContainer}>
                <Typography className={classes.title}>Add User</Typography>
                <Button
                  onClick={() => {
                    setIsAddingUser(true);
                  }}
                >
                  <img
                    alt={"Add User"}
                    src={`/icons/icon-plus.svg`}
                    className={classes.plus}
                  />
                </Button>
              </Box>
            </BorderContainer>
          ))}
      </Box>
      <TransferOwnershipModalV2
        visible={showTransferModal}
        setVisible={setShowTransferModal}
      />
    </>
  );
};
